import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  LOCALE_ID,
  inject,
  input,
  output
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { COIN_FORMATS, DefaultFormValueControl } from '@coin/shared/core/util-helpers';
import moment, { Moment, MomentInput } from 'moment';
import { InputSize, MatFormFieldClass } from '../../input.types';

type RangePart = 'start' | 'end';

@Component({
  selector: 'coin-v2-date-range-input',
  templateUrl: './v2-date-range-input.component.html',
  styleUrls: ['./v2-date-range-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': '"size-" + size()' },
  imports: [MatDatepickerModule, MatIconModule, MatFormField, MatLabel, MatSuffix, MatTooltip, FormsModule],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (injector: Injector) => {
        const inheritedFormats = injector.get(MAT_DATE_FORMATS, null, { skipSelf: true, optional: true });
        return { ...COIN_FORMATS, display: inheritedFormats?.display ?? COIN_FORMATS.display };
      },
      deps: [Injector]
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_LOCALE, useFactory: () => inject(LOCALE_ID).toString() }
  ]
})
export class V2DateRangeInputComponent extends DefaultFormValueControl<DateRange<Moment>> {
  readonly locale = inject(LOCALE_ID);

  readonly label = input<string>('');
  readonly startPlaceholder = input<string>('');
  readonly endPlaceholder = input<string>('');
  readonly matFormFieldClass = input<MatFormFieldClass>('customer-grey');
  readonly boldValue = input<boolean>(false);
  readonly size = input<InputSize>();
  readonly min = input<Moment>();
  readonly max = input<Moment>();
  readonly disableDays = input<number[]>();

  /**
   * * `input`: (default) the form value will update every time the input is updated
   * * `blur`: the form value will only update after the input loses focus
   */
  readonly updateOn = input<'input' | 'blur'>('input');

  readonly closed = output<void>();

  protected override toViewValue(value: DateRange<Moment>): DateRange<Moment> {
    return new DateRange<Moment>(this.normalizeDate(value?.start), this.normalizeDate(value?.end));
  }

  protected onInputChange(part: RangePart, value: MomentInput): void {
    if (this.updateOn() !== 'input') return;
    this.applyChange(part, value);
  }

  protected onInputBlur(part: RangePart, value: MomentInput): void {
    if (this.updateOn() === 'blur') this.applyChange(part, value);
    this.markAsTouched();
  }

  protected onPickerClosed(): void {
    this.markAsTouched();
    this.closed.emit();
  }

  protected readonly dayFilter = (d: Date): boolean => {
    const days = this.disableDays();
    if (days && d) {
      const day = new Date(d).getDay();
      return !days.includes(day);
    }
    return true;
  };

  public isSame(part: RangePart, value: MomentInput): boolean {
    const current = part === 'start' ? this.viewValue()?.start : this.viewValue()?.end;
    if (!value && !current) return true;
    return !!current?.isSame(value, 'day');
  }

  private applyChange(part: RangePart, value: MomentInput): void {
    if (this.isSame(part, value)) return;
    const next = value ? moment.utc(value) : null;
    const start = part === 'start' ? next : (this.viewValue()?.start ?? null);
    const end = part === 'end' ? next : (this.viewValue()?.end ?? null);
    this.updateValue(new DateRange<Moment>(start, end));
  }

  private normalizeDate(value: MomentInput | null | undefined): Moment | null {
    if (!value) return null;
    if (this.isInvalidDate(value)) return null;
    return moment.utc(value);
  }

  private isInvalidDate(value: MomentInput): boolean {
    // BE sometimes populates empty date fields with this default date string,
    // causing dates to appear as though they have a valid value.
    if (moment(value).toISOString() === moment('0001-01-01T00:00:00.000Z').toISOString()) {
      console.warn('Invalid date string provided!');
      return true;
    }
    return false;
  }
}
